import { useEffect, useRef, useState } from 'react';

export default function PricingTable() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    member: 0,
    nonMember: 0,
    courseMember: 0,
    courseNonMember: 0,
    botox: 0
  });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Start counting animation
          const duration = 1500;
          const steps = 60;
          const interval = duration / steps;
          
          const targets = {
            member: 1200,
            nonMember: 1500,
            courseMember: 1750,
            courseNonMember: 2000,
            botox: 3000
          };

          let currentStep = 0;
          const timer = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            
            setCounters({
              member: Math.floor(targets.member * progress),
              nonMember: Math.floor(targets.nonMember * progress),
              courseMember: Math.floor(targets.courseMember * progress),
              courseNonMember: Math.floor(targets.courseNonMember * progress),
              botox: Math.floor(targets.botox * progress)
            });

            if (currentStep >= steps) {
              clearInterval(timer);
              setCounters(targets);
            }
          }, interval);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div 
        className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h2 className="text-4xl font-bold text-[#0D1B3E] mb-4">Kayıt Ücretleri</h2>
        <p className="text-lg text-gray-600">Kongre kayıt ücretlerine %20 KDV dahildir.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Kayıt Ücretleri */}
        <div 
          className={`bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#0D1B3E] transition-all duration-700 ${
            isVisible ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-90 -rotate-3'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <div className="bg-gradient-to-r from-[#0D1B3E] to-[#1a2f5a] text-white py-6 px-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <i className="ri-price-tag-3-line text-3xl text-[#C9A84C]"></i>
              <h3 className="text-2xl font-bold">Kayıt Ücretleri</h3>
            </div>
            <p className="text-center text-gray-200 text-sm">Kongre Katılım Ücretleri</p>
          </div>
          <div className="p-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border-l-4 border-[#C9A84C] hover:shadow-md transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#C9A84C]/10 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-[#C9A84C] text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0D1B3E] text-base">Genç Diş Hekimliği Derneği Üyesi</p>
                    <p className="text-sm text-gray-500">Dernek üyelerine özel indirimli fiyat.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#0D1B3E]">{counters.member.toLocaleString('tr-TR')},00 TL</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border-l-4 border-[#0D1B3E] hover:shadow-md transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0D1B3E]/10 rounded-full flex items-center justify-center">
                    <i className="ri-user-follow-line text-[#0D1B3E] text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0D1B3E] text-base">Genç Diş Hekimliği Derneği Üyesi Olmayan</p>
                    <p className="text-sm text-gray-500">Standart katılım ücreti.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#0D1B3E]">{counters.nonMember.toLocaleString('tr-TR')},00 TL</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Uygulamalı Kurslar */}
        <div 
          className={`bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#1A9B8C] transition-all duration-700 ${
            isVisible ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-90 rotate-3'
          }`}
          style={{ transitionDelay: '400ms' }}
        >
          <div className="bg-gradient-to-r from-[#1A9B8C] to-[#15806f] text-white py-6 px-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <i className="ri-hand-heart-line text-3xl text-white"></i>
              <h3 className="text-2xl font-bold">Uygulamalı Kurslar</h3>
            </div>
            <p className="text-center text-white/90 text-sm">Pratik Eğitim Ücretleri</p>
          </div>
          <div className="p-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-teal-50 to-white rounded-xl border-l-4 border-[#1A9B8C] hover:shadow-md transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#1A9B8C]/10 rounded-full flex items-center justify-center">
                    <i className="ri-user-star-line text-[#1A9B8C] text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0D1B3E] text-base">Genç Diş Hekimliği Derneği Üyesi</p>
                    <p className="text-sm text-gray-500">Dernek üyelerine özel kurs fiyatı.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#1A9B8C]">{counters.courseMember.toLocaleString('tr-TR')},00 TL</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-teal-50 to-white rounded-xl border-l-4 border-[#0D1B3E] hover:shadow-md transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0D1B3E]/10 rounded-full flex items-center justify-center">
                    <i className="ri-user-add-line text-[#0D1B3E] text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0D1B3E] text-base">Genç Diş Hekimliği Derneği Üyesi Olmayan</p>
                    <p className="text-sm text-gray-500">Standart kurs katılım ücreti.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#0D1B3E]">{counters.courseNonMember.toLocaleString('tr-TR')},00 TL</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botulinum Toksin Kursu - Özel */}
      <div 
        className={`bg-white rounded-2xl shadow-xl overflow-hidden border-2 transition-all duration-700 ${
          isVisible ? 'opacity-100 scale-100 border-[#C9A84C]' : 'opacity-0 scale-95 border-transparent'
        }`}
        style={{ 
          transitionDelay: '600ms',
          animation: isVisible ? 'pulse-border 2s ease-in-out infinite' : 'none'
        }}
      >
        <div className="bg-gradient-to-r from-[#C9A84C] to-[#a88a3d] text-white py-6 px-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <i className="ri-syringe-line text-3xl text-white"></i>
            <h3 className="text-2xl font-bold">Diş Hekimliği Kliniğinde Botulinum Toksin Uygulamaları</h3>
          </div>
          <p className="text-center text-white/90 text-sm">Özel Uygulama Kursu</p>
        </div>
        <div className="p-8">
          <div className="flex items-center justify-between p-5 bg-gradient-to-r from-amber-50 to-white rounded-xl border-l-4 border-[#C9A84C] hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#C9A84C]/10 rounded-full flex items-center justify-center">
                <i className="ri-medal-line text-[#C9A84C] text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-[#0D1B3E] text-lg">Tüm Katılımcılar</p>
                <p className="text-sm text-gray-500">Botulinum Toksin Uygulamaları özel kursu.</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-[#C9A84C]">{counters.botox.toLocaleString('tr-TR')},00 TL</p>
            </div>
          </div>
        </div>
      </div>

      {/* Önemli Not */}
      <div 
        className={`mt-8 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-xl p-6 shadow-md transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
        style={{ transitionDelay: '800ms' }}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
            <i className="ri-information-line text-white text-xl"></i>
          </div>
          <div>
            <h4 className="font-bold text-red-900 text-lg mb-2">Önemli Bilgilendirme</h4>
            <p className="text-red-800 leading-relaxed mb-3">
              Kongre kayıt ücretlerine <strong>%20 KDV dahildir.</strong> Vergilerde oluşabilecek değişiklikler aynı şekilde yansıtılacaktır.
            </p>
            <p className="text-red-800 leading-relaxed">
              Tüm ücretler <strong>İdenta Event Organizasyon</strong> hesabına yatırılacaktır. Farklı bir hesaba yatırılan ücretler kabul edilmeyecektir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}